import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  FormControl,
  CircularProgress,
  Snackbar,
  SnackbarCloseReason,
} from '@mui/material';
import PasswordInput from '../../../components/Inputs/PasswordInput';
import StyledButton from '../../../components/Button/Button';
import Popup from '../../../components/Inputs/Popup';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import { changePassword } from '../../../services/NetworkServices';
import './Changepassword.css';
import { commonResponse } from '../../../services/types/APIResponseType';
import ApiStatus from '../../../config/apiStatusConstants';
import {
  getOTPVerificationToken,
  removeOTPVerificationToken,
} from '../../../utils/auth-token-utils/cookie';

const Changepassword: React.FC = () => {
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isToastOpen, setIsToastOpen] = useState<boolean>();

  const navigate = useNavigate();
  const token = getOTPVerificationToken();

  const onChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value);
  };

  const onChangeConfirmPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmNewPassword(event.target.value);
  };

  const handleContinue = () => {
    navigate('/signin');
  };

  const handlePasswordChange = async () => {
    if (!token) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg('Token is missing. Please try again.');
      setIsToastOpen(true);
      return;
    }

    setApiStatus(ApiStatus.LOADING);
    try {
      const response = await changePassword({
        newPassword,
        confirmNewPassword,
        token,
      });
      console.log('response is', response);
      const data: commonResponse = await response.data;
      if (data) {
        setApiStatus(ApiStatus.SUCCESS);
        removeOTPVerificationToken();
        setShowPopup(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      console.log('error is ', error);
      setErrorMsg(
        error.response.data.message ||
          'Something went wrong. Please try again.',
      );
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          onClick={handlePasswordChange}
          disabled={!(newPassword && confirmNewPassword)}
        >
          Change Password
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    if (apiStatus === ApiStatus.FAILURE) {
      return <p className="error-msg">{errorMsg}</p>;
    }
    return null;
  };

  const handleToastClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setIsToastOpen(false);
  };

  useEffect(() => {
    const otpVerified = getOTPVerificationToken();
    if (!otpVerified) {
      navigate('/forgotpassword');
    }
  }, [navigate]);

  return (
    <>
      {showPopup && <div className="backdrop"></div>} {/* Overlay backdrop */}
      <StyledBgContainer className={showPopup ? 'popup-active' : ''}>
        <TopNav showArrow={true} backRoute={'/signin'} />
        <Container maxWidth="sm">
          <Box component="form" noValidate autoComplete="off">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: { sm: 6 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontFamily: 'Opensans',
                  fontWeight: '700',
                }}
              >
                Change Password
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey', mt: 1, mb: 3 }}>
                Your new password must be different from previous password
              </Typography>
              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mb: 2, alignItems: 'center' }}
              >
                <PasswordInput
                  label="Set Password"
                  value={newPassword}
                  onChange={onChangePassword}
                />
              </FormControl>

              {(errorMsg.includes('uppercase') ||
                errorMsg ===
                  'New password should be no more than 18 characters long' ||
                errorMsg ===
                  'New password should be at least 8 characters long') && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    marginTop: '-16px',
                    width: { xs: '80vw', sm: '60vw', md: '60vw', lg: '40vw' },
                  }}
                >
                  {renderErrorMsg()}
                </Box>
              )}

              <FormControl
                fullWidth
                variant="outlined"
                sx={{ mb: 2, alignItems: 'center' }}
              >
                <PasswordInput
                  label="Retype Password"
                  value={confirmNewPassword}
                  onChange={onChangeConfirmPassword}
                />
              </FormControl>
              <Box
                className="error2-msg"
                sx={{
                  marginTop: '-16px',
                  width: { xs: '80vw', sm: '60vw', md: '60vw', lg: '40vw' },
                }}
              >
                {errorMsg.includes('Password should match') && renderErrorMsg()}
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 8, mb: 12 }}> {renderButton()}</Box>

          <Snackbar
            key={'top' + 'center'}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={2000}
            onClose={handleToastClose}
            open={isToastOpen}
            message={errorMsg}
            ContentProps={{
              style: {
                backgroundColor: 'red',
              },
            }}
          />
          {showPopup && (
            <Popup
              title=<Typography
                variant="h6"
                sx={{
                  fontFamily: 'opensans',
                  fontWeight: '700',
                  lineHeight: '1.4',
                }}
              >
                {' '}
                Password Reset <br /> Successfull{' '}
              </Typography>
              message=<Typography variant="body2">
                {' '}
                Your password has been reset successfully. You can now use your
                new password to log in.{' '}
              </Typography>
              buttonText="Done"
              onButtonClick={handleContinue}
            />
          )}
        </Container>
      </StyledBgContainer>
    </>
  );
};

export default Changepassword;
